const STORAGE_KEY = "bodyshop.mfa.session.v1";
const CHANNEL_NAME = "bodyshop-mfa-events-v1";

export const MFA_SESSION_CHANGED_EVENT = "bodyshop-mfa-session-changed";
export const MFA_POLICY_CHANGED_EVENT = "bodyshop-mfa-policy-changed";
export const MFA_CHALLENGE_REQUIRED_EVENT = "bodyshop-mfa-challenge-required";

let channel;

/**
 * Lazily creates the cross-tab channel used to propagate MFA state changes.
 *
 * @returns {BroadcastChannel|null} Shared channel when supported by the browser.
 */
const getChannel = () => {
  if (channel !== undefined) return channel;
  channel = typeof BroadcastChannel === "function" ? new BroadcastChannel(CHANNEL_NAME) : null;
  channel?.addEventListener("message", ({ data }) => {
    if (data?.type) window.dispatchEvent(new Event(data.type));
  });
  return channel;
};

/**
 * Notifies the current tab and other same-origin tabs of an MFA state change.
 *
 * @param {string} type Window event type to dispatch.
 * @returns {void}
 */
const dispatchMfaEvent = (type) => {
  window.dispatchEvent(new Event(type));
  getChannel()?.postMessage({ type });
};

/**
 * Reads the tab-scoped Bodyshop MFA session.
 *
 * @returns {string|null} Signed MFA session token when the tab is verified.
 */
export const getMfaSession = () => sessionStorage.getItem(STORAGE_KEY);

/**
 * Reads the expiry claim from an MFA session without treating the payload as verified authorization.
 *
 * @param {string|null} [token] Signed MFA session token.
 * @returns {number|null} Unix expiry time in seconds, or `null` for an invalid token.
 */
export const getMfaSessionExpiresAt = (token = getMfaSession()) => {
  try {
    const payload = token?.split(".")?.[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=")));
    return Number.isFinite(decoded.exp) ? decoded.exp : null;
  } catch {
    return null;
  }
};

/**
 * Stores or removes the tab-scoped MFA session and broadcasts the change.
 *
 * @param {string|null} token Signed session token, or `null` to clear it.
 * @returns {void}
 */
export const setMfaSession = (token) => {
  if (token) sessionStorage.setItem(STORAGE_KEY, token);
  else sessionStorage.removeItem(STORAGE_KEY);
  dispatchMfaEvent(MFA_SESSION_CHANGED_EVENT);
};

/**
 * Clears the current tab's MFA session.
 *
 * Shop switches suppress the change event because the page reload owns context restoration; broadcasting before
 * navigation can briefly remount transports with the previous shop ID.
 *
 * @param {Object} [options] Clear behavior.
 * @param {boolean} [options.notify=true] Whether active transports should immediately react to the change.
 * @returns {void}
 */
export const clearMfaSession = ({ notify = true } = {}) => {
  sessionStorage.removeItem(STORAGE_KEY);
  if (notify) dispatchMfaEvent(MFA_SESSION_CHANGED_EVENT);
};

/** Broadcasts that the active shop's MFA policy changed. */
export const notifyMfaPolicyChanged = () => dispatchMfaEvent(MFA_POLICY_CHANGED_EVENT);

/** Broadcasts that a protected request requires a fresh MFA challenge. */
export const notifyMfaChallengeRequired = () => dispatchMfaEvent(MFA_CHALLENGE_REQUIRED_EVENT);
